import { AlertTriangle, CalendarDays, CheckCircle2, Filter, FolderKanban, UserRound } from "lucide-react";

const TASK_FILTERS = [
  { id: "all", label: "All Tasks" },
  { id: "mine", label: "My Tasks" },
  { id: "overdue", label: "Overdue" },
  { id: "week", label: "Due This Week" },
  { id: "blocked", label: "Blocked" },
  { id: "unassigned", label: "Unassigned" },
];

const formatDate = (value) => {
  if (!value) return "No due date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

export const TaskWorkspace = ({ tasks, filter, onChangeFilter, onOpenTask, currentUser, exportTasksUrl, exportProjectsUrl }) => {
  const visibleTasks = tasks.filter((task) => {
    switch (filter) {
      case "mine":
        return task.assignee && (task.assignee === currentUser?.display_name || task.assignee === currentUser?.username);
      case "overdue":
        return task.is_overdue;
      case "week":
        return task.is_due_this_week;
      case "blocked":
        return task.status === "Blocked";
      case "unassigned":
        return !task.assignee;
      default:
        return true;
    }
  });

  return (
    <div data-testid="task-workspace">
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">Task Workspace</h1>
          <p className="text-sm text-zinc-500 mt-1">Personal workload, overdue work, blocked items, and export-ready delivery views.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a className="btn-secondary inline-flex items-center gap-2" href={exportTasksUrl}>
            Export Tasks CSV
          </a>
          <a className="btn-secondary inline-flex items-center gap-2" href={exportProjectsUrl}>
            Export Projects CSV
          </a>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        {TASK_FILTERS.map((item) => (
          <button
            key={item.id}
            className={`px-4 py-2 border text-sm font-semibold transition ${filter === item.id ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-500"}`}
            onClick={() => onChangeFilter(item.id)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleTasks.length === 0 ? (
          <div className="card p-8 text-sm text-zinc-400">No tasks match the current filter.</div>
        ) : (
          visibleTasks.map((task) => (
            <button className="card p-5 text-left transition hover:border-zinc-400 hover:bg-zinc-50" key={task.id} onClick={() => onOpenTask(task.project_id, task.id)} type="button">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-zinc-900">{task.title}</div>
                  <div className="mt-1 text-sm text-zinc-500">{task.project_name} · {task.company_name}</div>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">{task.status}</span>
              </div>
              <div className="mt-4 space-y-2 text-sm text-zinc-600">
                <div className="flex items-center gap-2"><UserRound size={14} className="text-zinc-400" /> {task.assignee || "Unassigned"}</div>
                <div className="flex items-center gap-2"><CalendarDays size={14} className="text-zinc-400" /> {formatDate(task.due_date)}</div>
                <div className="flex items-center gap-2"><FolderKanban size={14} className="text-zinc-400" /> {task.project_status || "Unknown project status"}</div>
                <div className="flex items-center gap-2"><Filter size={14} className="text-zinc-400" /> {(task.tags || []).join(", ") || "No tags"}</div>
              </div>
              <div className="mt-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em]">
                {task.is_overdue ? <span className="text-red-500 inline-flex items-center gap-1"><AlertTriangle size={12} /> Overdue</span> : null}
                {task.status === "Done" ? <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Done</span> : null}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
